/**
 * API Client
 * Handles communication with RAGSense public chat API
 *
 * Chat endpoints require a short-lived widget session token, minted at
 * /api/public/chat/session from the public config token (plus a Turnstile
 * token when the server has Turnstile configured). The client mints lazily,
 * refreshes before expiry, and re-bootstraps once on a 401.
 */

const SESSION_REFRESH_MARGIN_MS = 30000;

export class WidgetConfigError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'WidgetConfigError';
    this.code = code;
    this.status = status;
  }
}

export class APIClient {
  constructor(apiBaseUrl, configToken, sessionId, visitorId, clientScope = 'public', turnstileProvider = null) {
    this.apiBaseUrl = apiBaseUrl;
    this.configToken = configToken;
    this.sessionId = sessionId;
    this.visitorId = visitorId;
    this.clientScope = clientScope;
    this.turnstileProvider = turnstileProvider;
    this.sessionToken = null;
    this.sessionTokenExpiresAt = 0;
    this.sessionPromise = null;
  }

  baseHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-Widget-Token': this.configToken,
      'X-Session-ID': this.sessionId
    };
  }

  clearSessionToken() {
    this.sessionToken = null;
    this.sessionTokenExpiresAt = 0;
    this.sessionPromise = null;
  }

  /**
   * Ensure a valid widget session token, minting or refreshing as needed.
   * Concurrent callers share one in-flight bootstrap.
   */
  async ensureSession() {
    if (this.sessionToken && Date.now() < this.sessionTokenExpiresAt - SESSION_REFRESH_MARGIN_MS) {
      return;
    }
    if (!this.sessionPromise) {
      this.sessionPromise = this.createSession().finally(() => {
        this.sessionPromise = null;
      });
    }
    return this.sessionPromise;
  }

  async createSession() {
    let turnstileToken = null;
    if (this.turnstileProvider) {
      turnstileToken = await this.turnstileProvider.getToken();
    }

    const response = await fetch(`${this.apiBaseUrl}/api/public/chat/session`, {
      method: 'POST',
      headers: this.baseHeaders(),
      body: JSON.stringify({
        session_id: this.sessionId,
        turnstile_token: turnstileToken
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || error.detail || `HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!data.session_token) {
      throw new Error('Widget session bootstrap returned no token');
    }
    this.sessionToken = data.session_token;
    this.sessionTokenExpiresAt = Date.now() + (Number(data.expires_in) || 60) * 1000;
  }

  /**
   * POST to a session-protected endpoint. Retries once with a fresh session
   * token on 401 (expired token, rotated session, restarted server).
   */
  async postWithSession(path, body) {
    await this.ensureSession();

    const doFetch = () => fetch(`${this.apiBaseUrl}${path}`, {
      method: 'POST',
      headers: {
        ...this.baseHeaders(),
        'X-Widget-Session': this.sessionToken
      },
      body: JSON.stringify(body)
    });

    let response = await doFetch();
    if (response.status === 401) {
      this.clearSessionToken();
      await this.ensureSession();
      response = await doFetch();
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || error.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Send a chat message
   */
  async sendMessage(query, conversationId, browserFingerprint, utmParams, resolvedLanguage, conversationHistory = []) {
    return this.postWithSession('/api/public/chat/message', {
      query,
      session_id: this.sessionId,
      conversation_id: conversationId,
      conversation_history: conversationHistory,
      visitor_id: this.visitorId,
      client_scope: this.clientScope,
      browser_fingerprint: browserFingerprint,
      fingerprint_version: browserFingerprint?.version_tag || 'passive-v1',
      referrer_url: window.location.href,
      utm_params: utmParams,
      resolved_language: resolvedLanguage,
      accept_language: Array.isArray(navigator.languages) ? navigator.languages.join(',') : navigator.language
    });
  }

  /**
   * Submit feedback on a message
   */
  async submitFeedback(messageId, feedbackType, comment = null) {
    return this.postWithSession('/api/public/chat/feedback', {
      message_id: messageId,
      session_id: this.sessionId,
      feedback_type: feedbackType,
      comment
    });
  }

  /**
   * Get widget configuration
   */
  static async getConfig(apiBaseUrl, configToken) {
    let response;
    try {
      // A GET with no body does not need Content-Type. Sending
      // application/json here forced browsers to issue a CORS preflight,
      // which can be challenged by an edge/WAF before it reaches the API.
      response = await fetch(`${apiBaseUrl}/api/public/chat/config/${configToken}`, {
        method: 'GET',
        credentials: 'omit'
      });
    } catch (error) {
      throw new WidgetConfigError(
        'config_blocked',
        error?.message || 'Widget configuration request was blocked'
      );
    }

    const challenged = response.headers?.get?.('cf-mitigated') === 'challenge';

    if (!response.ok) {
      const rawBody = await response.text().catch(() => '');
      let parsedError = null;

      if (rawBody) {
        try {
          parsedError = JSON.parse(rawBody);
        } catch (_error) {
          parsedError = null;
        }
      }

      const detail =
        parsedError?.error ||
        parsedError?.detail ||
        rawBody ||
        response.statusText ||
        'Unknown error';

      const challengeBody = /just a moment|cdn-cgi\/challenge|cloudflare/i.test(rawBody);
      if (challenged || challengeBody) {
        throw new WidgetConfigError(
          'cloudflare_challenge',
          `HTTP ${response.status}: configuration request was challenged`,
          response.status
        );
      }

      const errorCode = parsedError?.code === 'DOMAIN_NOT_ALLOWED' ||
        (response.status === 403 && /domain not allowed/i.test(String(detail)))
        ? 'domain_rejected'
        : 'config_blocked';

      throw new WidgetConfigError(errorCode, `HTTP ${response.status}: ${detail}`, response.status);
    }

    return await response.json();
  }

  /**
   * Report a bounded initialization event through an image beacon. Image
   * requests need neither CORS response access nor a preflight, so telemetry
   * still has a chance to arrive when the configuration fetch is blocked.
   */
  static reportInitEvent(apiBaseUrl, configToken, event, reason = null) {
    if (typeof Image === 'undefined' || typeof window === 'undefined') return;
    try {
      const endpoint = new URL(
        `${apiBaseUrl}/api/public/chat/telemetry/${encodeURIComponent(configToken)}/pixel.gif`
      );
      endpoint.searchParams.set('event', event);
      endpoint.searchParams.set('page_host', window.location.hostname || 'unknown');
      if (reason) endpoint.searchParams.set('reason', String(reason).slice(0, 64));
      const beacon = new Image();
      beacon.src = endpoint.toString();
    } catch (_error) {
      // Telemetry is best-effort and must never affect widget behavior.
    }
  }
}
